import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
  TouchableOpacity,
} from "react-native";
import { platformAlert } from "../utils/platformAlert";
import { Feather } from "@expo/vector-icons";
import { supabase } from "../lib/supabase";
import { Vendor } from "../types/vendor";
import { useColors, ThemeColors } from "../hooks/useColors";
import { useGeocoding } from "../hooks/useGeocoding";
import GeocodingService from "../services/GeocodingService";
import { usePhoneVerification } from "../hooks/usePhoneVerification";
import { PhoneVerification } from "./ui/PhoneVerification";
import { formatPhoneNumberForAPI } from "../utils/phoneValidation";

interface AddressSettingsProps {
  vendor: Vendor;
  onUpdate?: () => void;
}

export const AddressSettings: React.FC<AddressSettingsProps> = ({
  vendor,
  onUpdate,
}) => {
  const colors = useColors();
  const styles = createStyles(colors);
  const { geocodeAddress } = useGeocoding();
  const [formData, setFormData] = useState({
    street_line1: vendor.street_line1 || "",
    apartment: vendor.apartment || "",
    city: vendor.city || "",
    state: vendor.state || "",
    zip_code: vendor.zip_code || "",
    country: vendor.country || "US",
    phone_number: vendor.phone_number || vendor.phone || "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isGeocodingLoading, setIsGeocodingLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [geocodeResult, setGeocodeResult] = useState<{
    latitude: number;
    longitude: number;
    formatted_address: string;
  } | null>(null);
  // Simple flag: true = has unsaved changes, false = no changes (same pattern as StoreHours)
  const [addressHasChanges, setAddressHasChanges] = useState(false);
  
  // Ref to prevent double-execution (synchronous, survives re-renders)
  const isSavingAddressRef = useRef(false);

  // Track if phone was already verified (from database)
  const [phoneAlreadyVerified, setPhoneAlreadyVerified] = useState(
    vendor.phone_verified === true
  );

  // Sync phoneAlreadyVerified when vendor data refreshes (e.g., after navigation)
  useEffect(() => {
    if (vendor.phone_verified === true) {
      setPhoneAlreadyVerified(true);
    }
  }, [vendor.phone_verified]);

  // Phone verification - update phoneAlreadyVerified when verification completes
  const phoneVerification = usePhoneVerification({
    context: "vendor",
    userId: vendor.user_id,
    onVerified: () => {
      // When phone is verified, mark it as verified and trigger hasChanges
      setPhoneAlreadyVerified(true);
      setHasChanges(true);
    },
  });

  // Initialize geocoding result if vendor already has coordinates
  useEffect(() => {
    const hasValidCoordinates = GeocodingService.areCoordinatesValid(
      vendor.latitude,
      vendor.longitude
    );
    if (hasValidCoordinates && !geocodeResult) {
      setGeocodeResult({
        latitude: vendor.latitude!,
        longitude: vendor.longitude!,
        formatted_address:
          vendor.address ||
          `${vendor.street_line1 || ""}, ${vendor.city || ""}, ${
            vendor.state || ""
          } ${vendor.zip_code || ""}`.trim(),
      });
    }
  }, [vendor, geocodeResult]);

  // Track changes to enable/disable save button
  useEffect(() => {
    const currentData = {
      street_line1: vendor.street_line1 || "",
      apartment: vendor.apartment || "",
      city: vendor.city || "",
      state: vendor.state || "",
      zip_code: vendor.zip_code || "",
      country: vendor.country || "US",
      phone_number: vendor.phone_number || vendor.phone || "",
    };

    const hasChanged = JSON.stringify(formData) !== JSON.stringify(currentData);
    setHasChanges(hasChanged);
  }, [formData, vendor]);

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // Track address field changes - mark as having unsaved changes
    if (
      ["street_line1", "apartment", "city", "state", "zip_code"].includes(field)
    ) {
      setAddressHasChanges(true);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.street_line1.trim()) {
      newErrors.street_line1 = "Street address is required";
    }
    if (!formData.city.trim()) {
      newErrors.city = "City is required";
    }
    if (!formData.state.trim()) {
      newErrors.state = "State is required";
    }
    if (!formData.zip_code.trim()) {
      newErrors.zip_code = "ZIP code is required";
    }
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required for delivery coordination";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manual address verification and save function - matches StoreHoursSettings pattern
  const handleGeocodeAddress = async () => {
    // Synchronous ref check to prevent double-execution across re-renders
    if (isSavingAddressRef.current) {
      console.log("[VENDOR-ADDRESS] Save already in progress, blocking duplicate");
      return;
    }
    
    if (!vendor?.id) return;

    if (!formData.street_line1 || !formData.city || !formData.state || !formData.zip_code) {
      platformAlert(
        "Error",
        "Please fill in street address, city, state, and ZIP code before saving"
      );
      return;
    }

    isSavingAddressRef.current = true;
    setIsGeocodingLoading(true);
    try {
      const addressString = GeocodingService.buildAddressString({
        street_address: formData.street_line1,
        apartment: formData.apartment,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        country: formData.country || "US",
      });

      console.log("[VENDOR-ADDRESS] Geocoding address:", addressString);
      const result = await geocodeAddress(addressString);

      if (result) {
        // Save address to database immediately after successful geocoding
        const { error } = await supabase
          .from("vendors")
          .update({
            street_line1: formData.street_line1.trim(),
            apartment: formData.apartment.trim() || null,
            city: formData.city.trim(),
            state: formData.state.trim(),
            zip_code: formData.zip_code.trim(),
            country: formData.country.trim(),
            address: addressString,
            latitude: result.latitude,
            longitude: result.longitude,
            updated_at: new Date().toISOString(),
          })
          .eq("id", vendor.id);

        if (error) throw error;

        setGeocodeResult(result);
        setAddressHasChanges(false);
        onUpdate?.();
        console.log("[VENDOR-ADDRESS] Address verified and saved:", result);
        platformAlert(
          "Address Saved",
          `Your pickup address has been verified and saved:\n\n${result.formatted_address}`,
          [{ text: "OK" }]
        );
      } else {
        platformAlert(
          "Location Not Found",
          "Could not find the location for this address. Please check the address and try again."
        );
      }
    } catch (error) {
      console.error("[VENDOR-ADDRESS] Error saving address:", error);
      platformAlert(
        "Error",
        "Error saving address. Please try again."
      );
    } finally {
      isSavingAddressRef.current = false;
      setIsGeocodingLoading(false);
    }
  };

  const handleSaveContact = useCallback(async () => {
    // Validate phone number is present
    if (!formData.phone_number.trim()) {
      platformAlert("Error", "Please enter your phone number.");
      return;
    }

    // Block saving if phone is not verified
    if (!phoneVerification.isVerified && !phoneAlreadyVerified) {
      platformAlert(
        "Phone Verification Required",
        "Please verify your phone number before saving. This ensures you can receive order notifications.",
        [{ text: "OK" }]
      );
      return;
    }

    setIsLoading(true);

    try {
      // Include phone verification status in save
      const isPhoneVerified = phoneVerification.isVerified || phoneAlreadyVerified;
      
      // Format phone number to E.164 format (+1XXXXXXXXXX) for SMS compatibility
      const formattedPhone = formatPhoneNumberForAPI(formData.phone_number.trim());
      
      const updateData = {
        phone_number: formattedPhone,
        phone_verified: isPhoneVerified,
        phone_verified_at: isPhoneVerified ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
      };

      console.log("📞 [VENDOR-CONTACT-UPDATE] Saving phone details");

      // Update vendor contact in database
      const { error } = await supabase
        .from("vendors")
        .update(updateData)
        .eq("id", vendor.id);

      if (error) {
        throw error;
      }

      console.log("✅ [VENDOR-CONTACT-UPDATE] Contact updated successfully");

      platformAlert(
        "Success",
        "Contact details saved successfully!"
      );

      // Update formData with the formatted phone to match what's in the database
      // This prevents hasChanges from being true due to phone format mismatch
      setFormData(prev => ({ ...prev, phone_number: formattedPhone }));

      // Mark phone as verified in local state so it persists
      if (phoneVerification.isVerified) {
        setPhoneAlreadyVerified(true);
      }
      
      // Reset the phone verification flow (but keep phoneAlreadyVerified true)
      phoneVerification.reset();
      
      setHasChanges(false);
      onUpdate?.();
    } catch (error: any) {
      console.error(
        "❌ [VENDOR-CONTACT-UPDATE] Failed to update contact:",
        error
      );
      platformAlert(
        "Error",
        error.message || "Failed to save contact details. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  }, [formData.phone_number, vendor.id, onUpdate, phoneVerification, phoneAlreadyVerified]);

  return (
    <View style={styles.container}>
      <ScrollView contentContainerStyle={styles.scroll}>
        <SectionCard colors={colors}>
          <FormField
            label="Street Address"
            value={formData.street_line1}
            onChangeText={(text) => handleInputChange("street_line1", text)}
            error={errors.street_line1}
            placeholder="123 Main Street"
            required
            colors={colors}
          />

          <FormField
            label="Apartment/Suite"
            value={formData.apartment}
            onChangeText={(text) => handleInputChange("apartment", text)}
            placeholder="Apt 4B, Suite 100"
            colors={colors}
          />

          <FormField
            label="City"
            value={formData.city}
            onChangeText={(text) => handleInputChange("city", text)}
            error={errors.city}
            placeholder="New York"
            required
            colors={colors}
          />

          <View style={styles.row}>
            <View style={[styles.flex1, styles.marginRight]}>
              <FormField
                label="State"
                value={formData.state}
                onChangeText={(text) => handleInputChange("state", text)}
                error={errors.state}
                placeholder="NY"
                maxLength={2}
                required
                colors={colors}
                autoCapitalize="characters"
              />
            </View>
            <View style={styles.flex1}>
              <FormField
                label="ZIP Code"
                value={formData.zip_code}
                onChangeText={(text) => handleInputChange("zip_code", text)}
                error={errors.zip_code}
                placeholder="10001"
                keyboardType="numeric"
                maxLength={10}
                required
                colors={colors}
              />
            </View>
          </View>

          {/* Address Save Section */}
          {formData.street_line1 && formData.city && formData.state && (
            <View style={styles.geocodingSection}>
              {!geocodeResult && (
                <View style={styles.verificationRequired}>
                  <Feather
                    name="alert-circle"
                    size={16}
                    color={colors.warning || colors.primary}
                  />
                  <Text style={styles.verificationRequiredText}>
                    Please save your address to continue
                  </Text>
                </View>
              )}

              <TouchableOpacity
                onPress={handleGeocodeAddress}
                style={[
                  styles.geocodeButton,
                  !addressHasChanges && { opacity: 0.6 },
                ]}
                disabled={isGeocodingLoading || !addressHasChanges}
                activeOpacity={0.7}
              >
                <View style={styles.geocodeButtonContent}>
                  {isGeocodingLoading ? (
                    <ActivityIndicator size="small" color={colors.surface} />
                  ) : (
                    <Feather
                      name={!addressHasChanges && geocodeResult ? "check-circle" : "map-pin"}
                      size={16}
                      color={colors.surface}
                    />
                  )}
                  <Text style={styles.geocodeButtonText}>
                    {isGeocodingLoading
                      ? "Saving Address..."
                      : !addressHasChanges && geocodeResult
                      ? "Address Saved"
                      : "Save Address"}
                  </Text>
                </View>
              </TouchableOpacity>

              {geocodeResult && (
                <View
                  style={[
                    styles.geocodeResult,
                    addressHasChanges && styles.geocodeResultChanged,
                  ]}
                >
                  <View style={styles.geocodeResultHeader}>
                    <Feather
                      name={addressHasChanges ? "alert-circle" : "check-circle"}
                      size={16}
                      color={
                        addressHasChanges
                          ? colors.warning || colors.primary
                          : colors.success
                      }
                    />
                    <Text
                      style={[
                        styles.geocodeResultTitle,
                        addressHasChanges && styles.geocodeResultTitleChanged,
                      ]}
                    >
                      {addressHasChanges
                        ? "Address Changed - Please Save Again"
                        : "Address Saved"}
                    </Text>
                  </View>
                  <Text style={styles.geocodeResultAddress}>
                    {geocodeResult.formatted_address}
                  </Text>
                  {addressHasChanges && (
                    <Text style={styles.geocodeResultWarning}>
                      Please save your address again to update.
                    </Text>
                  )}
                </View>
              )}
            </View>
          )}
        </SectionCard>

        <SectionCard title="Contact & Delivery" colors={colors}>
          <PhoneVerification
            phoneNumber={formData.phone_number}
            onPhoneChange={(phone) => {
              handleInputChange("phone_number", phone);
              // Reset verification if phone changes and was previously verified
              if (phoneAlreadyVerified) {
                setPhoneAlreadyVerified(false);
              }
            }}
            isVerified={phoneVerification.isVerified || phoneAlreadyVerified}
            codeSent={phoneVerification.codeSent}
            isSending={phoneVerification.isSending}
            isVerifying={phoneVerification.isVerifying}
            error={phoneVerification.error || errors.phone_number || null}
            onSendCode={() => phoneVerification.sendVerificationCode(formData.phone_number)}
            onVerifyCode={(code) => phoneVerification.verifyCode(formData.phone_number, code)}
            onReset={() => {
              phoneVerification.reset();
              setPhoneAlreadyVerified(false);
              setHasChanges(true); // Mark as having changes when resetting verification
            }}
            label="Phone Number"
            placeholder="+1 (555) 123-4567"
          />
        </SectionCard>
      </ScrollView>

      <PrimaryButton
        title={
          isLoading
            ? "Saving..."
            : formData.phone_number.trim().length > 0 &&
              !phoneVerification.isVerified &&
              !phoneAlreadyVerified
            ? "Verify Phone to Save"
            : "Save Details"
        }
        onPress={handleSaveContact}
        disabled={
          isLoading ||
          !hasChanges ||
          Boolean(
            formData.phone_number.trim().length > 0 &&
            !phoneVerification.isVerified &&
            !phoneAlreadyVerified
          )
        }
        saved={!hasChanges}
        colors={colors}
      />
    </View>
  );
};

// Helper Components (matching StorefrontCustomization pattern)
const SectionCard = ({
  title,
  children,
  colors,
}: {
  title?: string;
  children: React.ReactNode;
  colors: ThemeColors;
}) => {
  const cardStyles = StyleSheet.create({
    card: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 16,
      marginBottom: 16,
      shadowColor: colors.shadow,
      shadowOpacity: 0.1,
      shadowRadius: 6,
      elevation: 3,
      borderWidth: 1,
      borderColor: colors.border,
    },
    cardTitle: {
      fontSize: 18,
      fontWeight: "600",
      marginBottom: 12,
      color: colors.text,
    },
  });

  return (
    <View style={cardStyles.card}>
      {title ? <Text style={cardStyles.cardTitle}>{title}</Text> : null}
      {children}
    </View>
  );
};

const FormField = ({
  label,
  error,
  value,
  onChangeText,
  placeholder,
  multiline,
  numberOfLines,
  keyboardType,
  maxLength,
  required,
  colors,
  autoCapitalize = "sentences",
  style,
}: {
  label: string;
  error?: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  multiline?: boolean;
  numberOfLines?: number;
  keyboardType?: any;
  maxLength?: number;
  required?: boolean;
  colors: ThemeColors;
  autoCapitalize?: "none" | "sentences" | "words" | "characters";
  style?: any;
}) => {
  const fieldStyles = StyleSheet.create({
    field: {
      marginBottom: 16,
    },
    label: {
      fontWeight: "500",
      marginBottom: 6,
      color: colors.text,
    },
    required: {
      color: colors.error,
    },
    input: {
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      color: colors.text,
    },
    inputError: {
      borderColor: colors.error,
    },
    error: {
      color: colors.error,
      marginTop: 4,
      fontSize: 12,
    },
  });

  return (
    <View style={[fieldStyles.field, style]}>
      <Text style={fieldStyles.label}>
        {label}
        {required && <Text style={fieldStyles.required}> *</Text>}
      </Text>
      <TextInput
        style={[fieldStyles.input, error ? fieldStyles.inputError : null]}
        placeholder={placeholder}
        placeholderTextColor={colors.textLight}
        value={value}
        onChangeText={onChangeText}
        multiline={multiline}
        numberOfLines={numberOfLines}
        keyboardType={keyboardType}
        maxLength={maxLength}
        autoCapitalize={keyboardType === "phone-pad" ? "none" : autoCapitalize}
      />
      {error && <Text style={fieldStyles.error}>{error}</Text>}
    </View>
  );
};

const PrimaryButton = ({
  title,
  onPress,
  disabled,
  saved,
  colors,
}: {
  title: string;
  onPress: () => void;
  disabled?: boolean;
  saved?: boolean;
  colors: ThemeColors;
}) => {
  const buttonStyles = StyleSheet.create({
    button: {
      position: "absolute",
      bottom: 16,
      left: 16,
      right: 16,
      backgroundColor: colors.success,
      paddingVertical: 14,
      borderRadius: 8,
      alignItems: "center",
      justifyContent: "center",
    },
    buttonDisabled: {
      opacity: 0.6,
    },
    buttonText: {
      color: colors.surface,
      fontSize: 17,
      fontWeight: "600",
    },
  });

  return (
    <TouchableOpacity
      style={[buttonStyles.button, (disabled || saved) && buttonStyles.buttonDisabled]}
      onPress={onPress}
      disabled={disabled}
      activeOpacity={0.7}
    >
      <Text style={buttonStyles.buttonText}>{title}</Text>
    </TouchableOpacity>
  );
};

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    scroll: {
      padding: 16,
      paddingBottom: 80,
    },
    row: {
      flexDirection: "row",
      alignItems: "flex-start",
    },
    flex1: {
      flex: 1,
    },
    marginRight: {
      marginRight: 8,
    },
    geocodingStatus: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      padding: 12,
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 8,
      marginBottom: 16,
    },
    geocodingText: {
      color: colors.text,
      marginLeft: 8,
      fontSize: 14,
    },
    geocodingSection: {
      gap: 12,
      marginTop: 16,
    },
    verificationRequired: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      paddingVertical: 8,
    },
    verificationRequiredText: {
      fontSize: 14,
      color: colors.warning || colors.primary,
      fontWeight: "500",
    },
    geocodeButton: {
      backgroundColor: colors.success,
      borderRadius: 8,
      padding: 12,
      alignItems: "center",
    },
    geocodeButtonContent: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    geocodeButtonText: {
      color: colors.surface,
      fontSize: 16,
      fontWeight: "600",
    },
    geocodeButtonTextVerified: {
      color: colors.surface,
    },
    geocodeResult: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 12,
      gap: 8,
      borderWidth: 1,
      borderColor: colors.success,
    },
    geocodeResultChanged: {
      borderColor: colors.warning || colors.primary,
      backgroundColor: colors.backgroundSecondary || colors.surface,
    },
    geocodeResultHeader: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    geocodeResultTitle: {
      color: colors.success,
      fontSize: 16,
      fontWeight: "600",
    },
    geocodeResultTitleChanged: {
      color: colors.warning || colors.primary,
    },
    geocodeResultAddress: {
      fontSize: 14,
      color: colors.text,
      fontStyle: "italic",
      marginTop: 4,
    },
    geocodeResultWarning: {
      fontSize: 13,
      color: colors.warning || colors.primary,
      fontWeight: "500",
      marginTop: 4,
    },
  });
